/**
 * StatTable
 *
 * The one stat table for the app. Columns are stats, rows are time windows
 * ("Last 7 Games", "Last 30 Games", "Season" — spelled out in full, never
 * "L7"/"L30"). A window with no data is omitted entirely rather than shown
 * as a dashed row, since a sport can genuinely lack a window (NFL has no
 * last-7/last-30 split on the current data plan) — that's the caller's call,
 * made by simply not including the row.
 *
 * Values are right-aligned mono, one decimal for numbers. Max 5 columns —
 * the caller picks which 5 stats matter for the context; this just enforces
 * the cap defensively.
 */

import { V2 } from './JaiV2'
import { JAI_FONTS } from './JaiBrand'

function formatValue(val) {
  if (val == null || val === '—') return '—'
  if (typeof val === 'number') return val.toFixed(1)
  return val
}

// Each data column needs at least this much room for its header label
// (mono, uppercase, letter-spacing) and a right-aligned value like "247.0"
// not to wrap or clip. Below this, CSS grid's default `1fr` track (which
// bottoms out at the content's min-content width, not zero) can force the
// whole grid wider than its container — that's the page-level horizontal
// scroll the iOS pass is required to eliminate. NFL labels like "PASS YDS"
// are the widest case (see config/statColumns.js).
const MIN_COL_PX = 72
const MIN_LABEL_COL_PX = 84

export default function StatTable({ title = 'Stats', columns, rows }) {
  const cols = (columns || []).slice(0, 5)
  const dataRows = (rows || []).filter(r => r && r.values)

  if (cols.length === 0 || dataRows.length === 0) return null

  const grid = `${MIN_LABEL_COL_PX}px repeat(${cols.length}, minmax(${MIN_COL_PX}px, 1fr))`
  const minTotalWidth = MIN_LABEL_COL_PX + cols.length * MIN_COL_PX

  return (
    <div style={{
      background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 16,
      marginBottom: 14, overflow: 'hidden', boxShadow: V2.cardShadow,
    }}>
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${V2.hairSoft}` }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{title}</span>
      </div>

      {/* On a narrow phone, five stat columns plus the label column can need
          more width than the screen has. Rather than shrinking text into
          illegibility or wrapping headers, this scrolls horizontally inside
          its own box — the page itself never gains a scrollbar. See the
          "wide-table rule" in engineering/ios/IOS_OVERNIGHT_RUN.md Phase 3. */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: minTotalWidth }}>
          {/* Header row */}
          <div style={{
            display: 'grid', gridTemplateColumns: grid, padding: '9px 20px',
            borderBottom: `1px solid ${V2.hairSoft}`, fontSize: 10, fontWeight: 700,
            letterSpacing: '0.1em', color: V2.faint, fontFamily: JAI_FONTS.mono, textTransform: 'uppercase',
          }}>
            <span />
            {cols.map(col => (
              <span key={col.key} style={{ textAlign: 'right' }}>{col.label}</span>
            ))}
          </div>

          {/* Data rows */}
          {dataRows.map((row, ri) => (
            <div
              key={row.label}
              style={{
                display: 'grid', gridTemplateColumns: grid, padding: '11px 20px',
                borderBottom: ri < dataRows.length - 1 ? `1px solid ${V2.hairSoft}` : 'none',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: V2.navy, display: 'flex', alignItems: 'center', gap: 5 }}>
                {row.label}
                {row.trend && (
                  <span style={{ color: row.trend === 'hot' ? V2.success : V2.error, fontSize: 11 }}>
                    {row.trend === 'hot' ? '▲' : '▼'}
                  </span>
                )}
              </span>
              {cols.map(col => {
                const val = formatValue(row.values[col.key])
                return (
                  <span
                    key={col.key}
                    style={{
                      textAlign: 'right', fontSize: 13.5, fontFamily: JAI_FONTS.mono,
                      fontWeight: val === '—' ? 400 : 700,
                      color: val === '—' ? 'rgba(10,31,68,0.3)' : V2.navy,
                    }}
                  >
                    {val}
                  </span>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
